import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Box,
    TextField,
    Button,
    Typography,
    useTheme,
    useMediaQuery,
    Stack,
    Slider,
} from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { PeopleSearch } from '../PeopleSearch/PeopleSearch';
import { IUserSummary } from '../../types/global/helper';
import { generateRandomCode } from '../../utils/stringUtils';
import CustomButton from '../Button/CustomButton';
import { DialogueModal } from '../DialogueModal/DialogueModal';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { useDispatch, useSelector } from 'react-redux';
import { createAffiliate } from '../../redux/affiliate';
import { selectAffiliateCreateLoading } from '../../redux/affiliate';

export interface IAffiliateCodeModalProps {
    open: boolean;
    onClose: () => void;
}

export interface IAffiliateCode {
    affiliate_code: string;
    username: string;
    expiration: string;
    percentage: number;
}

export const AffiliateCodeModal: React.FC<IAffiliateCodeModalProps> = ({ open, onClose }) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const dispatch = useDispatch();
    const createLoading = useSelector(selectAffiliateCreateLoading);
    const [selectedUser, setSelectedUser] = useState<IUserSummary | null>(null);
    const [affiliateCode, setAffiliateCode] = useState('');
    const [expirationMonths, setExpirationMonths] = useState<number>(1);
    const [percentage, setPercentage] = useState<number>(10);
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('xs'));

    // Calculate expiration date based on selected months
    const expirationDate = useMemo(() => {
        const date = new Date();
        date.setMonth(date.getMonth() + expirationMonths);
        return date.toISOString().split('T')[0];
    }, [expirationMonths]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setSelectedUser(null);
            setAffiliateCode('');
            setExpirationMonths(1);
            setPercentage(10);
        }
    }, [open]);

    const handleUserSelect = (users?: any) => {
        if (users && users.length > 0) {
            setSelectedUser(users[0]);
            setSearchDialogOpen(false);
        }
    };

    const handleAutoGenerate = () => {
        const generatedCode = generateRandomCode(8);
        setAffiliateCode(generatedCode);
    };

    const handleSave = async () => {
        if (!selectedUser || !affiliateCode.trim() || !expirationDate || !percentage) return;

        const affiliateData = {
            affiliate_code: affiliateCode,
            username: selectedUser.userName,
            expiration: expirationDate,
            percentage,
        };

        dispatch(createAffiliate(affiliateData));
        onClose();
    };

    const isFormValid = selectedUser && affiliateCode.trim() && expirationDate && percentage > 0;

    const peopleSearch = useMemo(() => {
        return (
            <DialogueModal
                open={searchDialogOpen}
                onClose={() => setSearchDialogOpen(false)}
                buttonEnabled={false}
                header="Select Recipients"
                paperSX={{
                    height: { xs: '100dvh', sm: '70dvh' },
                    width: '100vw',
                    maxWidth: {
                        xs: '90dvw',
                        sm: '500px',
                    },
                    maxHeight: {
                        xs: '70%',
                        sm: '100%',
                    },
                }}
                headerStyle={{ marginBottom: 1.5 }}
                dialogueProps={{
                    fullWidth: true,
                    open: searchDialogOpen,
                    maxWidth: 'xs',
                    fullScreen: isMobile,
                }}>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden', // Prevent outer container from scrolling
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto', // Make this box scrollabl
                        }}>
                        <PeopleSearch
                            value={[]}
                            onChange={handleUserSelect}
                            placeholder="Search user..."
                            singleSelection
                        />
                    </Box>
                </Box>
            </DialogueModal>
        );
    }, [searchDialogOpen, isMobile]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    background: theme[themeStyle].secondaryBg,
                    borderRadius: '16px',
                },
            }}>
            {peopleSearch}
            <DialogTitle
                sx={{
                    color: theme[themeStyle].filters_white,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '24px',
                    textAlign: 'center',
                    pb: 2,
                }}>
                Create Affiliate Code
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
                    {/* User Search */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].form_label,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '14px',
                            }}>
                            Select User
                        </Typography>
                        {selectedUser ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 2,
                                    backgroundColor: theme[themeStyle].form_fields,
                                    borderRadius: '8px',
                                    border: `1px solid ${theme[themeStyle].gray_sidebar}`,
                                }}>
                                <Stack display={'flex'} gap={1} direction={'row'} alignItems={'center'}>
                                    <ThspianAvatar user={selectedUser} />

                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].filters_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {selectedUser.firstName} {selectedUser.lastName}
                                    </Typography>
                                </Stack>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        setSelectedUser(null);
                                        setSearchDialogOpen(true);
                                    }}
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        borderColor: theme[themeStyle].highlight,
                                        '&:hover': {
                                            borderColor: theme[themeStyle].highlight,
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                    }}>
                                    Change
                                </Button>
                            </Box>
                        ) : (
                            <CustomButton label="Add User" onClick={() => setSearchDialogOpen(true)} />
                        )}
                    </Box>

                    {/* Affiliate Code Input */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].form_label,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '14px',
                            }}>
                            Affiliate Code
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth
                                placeholder="Enter affiliate code"
                                value={affiliateCode}
                                onChange={e => setAffiliateCode(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                }}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleAutoGenerate}
                                sx={{
                                    color: theme[themeStyle].highlight,
                                    borderColor: theme[themeStyle].highlight,
                                    minWidth: '120px',
                                    '&:hover': {
                                        borderColor: theme[themeStyle].highlight,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}>
                                Auto Generate
                            </Button>
                        </Box>
                    </Box>

                    {/* Expiration Date */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].form_label,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '14px',
                            }}>
                            Expiration Date - {expirationMonths} month{expirationMonths > 1 ? 's' : ''} (
                            {expirationDate})
                        </Typography>
                        <Box sx={{ px: 1 }}>
                            <Slider
                                value={expirationMonths}
                                onChange={(_, value) => setExpirationMonths(value as number)}
                                step={null}
                                marks={[
                                    { value: 1, label: '1M' },
                                    { value: 2, label: '2M' },
                                    { value: 4, label: '4M' },
                                    { value: 6, label: '6M' },
                                    { value: 12, label: '12M' },
                                ]}
                                min={1}
                                max={12}
                                sx={{
                                    '& .MuiSlider-track': {
                                        backgroundColor: theme[themeStyle].highlight,
                                    },
                                    '& .MuiSlider-rail': {
                                        backgroundColor: theme[themeStyle].form_divider,
                                    },
                                    '& .MuiSlider-thumb': {
                                        backgroundColor: theme[themeStyle].highlight,
                                        '&:hover': {
                                            boxShadow: `0 0 0 8px ${theme[themeStyle].highlight}20`,
                                        },
                                    },
                                    '& .MuiSlider-mark': {
                                        backgroundColor: theme[themeStyle].form_divider,
                                    },
                                    '& .MuiSlider-markLabel': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Percentage */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].form_label,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '14px',
                            }}>
                            Commission Percentage - {percentage}% (Max 40%)
                        </Typography>
                        <Box sx={{ px: 1 }}>
                            <Slider
                                value={percentage}
                                onChange={(_, value) => setPercentage(value as number)}
                                min={1}
                                max={40}
                                step={1}
                                marks={[
                                    { value: 1, label: '1%' },
                                    { value: 10, label: '10%' },
                                    { value: 20, label: '20%' },
                                    { value: 30, label: '30%' },
                                    { value: 40, label: '40%' },
                                ]}
                                sx={{
                                    '& .MuiSlider-track': {
                                        backgroundColor: theme[themeStyle].highlight,
                                    },
                                    '& .MuiSlider-rail': {
                                        backgroundColor: theme[themeStyle].form_divider,
                                    },
                                    '& .MuiSlider-thumb': {
                                        backgroundColor: theme[themeStyle].highlight,
                                        '&:hover': {
                                            boxShadow: `0 0 0 8px ${theme[themeStyle].highlight}20`,
                                        },
                                    },
                                    '& .MuiSlider-mark': {
                                        backgroundColor: theme[themeStyle].form_divider,
                                    },
                                    '& .MuiSlider-markLabel': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                    <CustomButton
                        label="Cancel"
                        onClick={onClose}
                        disabled={createLoading}
                        textStyle={{
                            color: theme[themeStyle].tag_white,
                        }}
                        sx={{
                            flex: 1,
                            backgroundColor: 'transparent',
                            border: `1px solid ${theme[themeStyle].tag_white}`,

                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    />
                    <CustomButton
                        label={createLoading ? 'Saving...' : 'Save'}
                        onClick={handleSave}
                        disabled={!isFormValid || createLoading}
                        sx={{
                            flex: 1,
                        }}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};
